import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useI18n } from "@/context/I18nContext";
import { findKeys } from "@/lib/i18n";

// Mode traduction : un clic sur un texte de la page ouvre la correction de sa traduction.
export const TranslateOverlay = () => {
  const { t, lang, editMode, setEditMode, saveEntry } = useI18n();
  const [items, setItems] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!editMode) return undefined;
    const onClick = (e) => {
      if (e.target.closest("[data-translate-ui]")) return;
      e.preventDefault();
      e.stopPropagation();
      let el = e.target;
      while (el && el !== document.body && !(el.innerText || el.placeholder || "").trim()) el = el.parentElement;
      const text = [el?.innerText, el?.placeholder, el?.title].filter(Boolean).join(" ").slice(0, 600);
      const found = findKeys(text, lang);
      if (!found.length) { toast.info(t("Aucun texte traduisible trouvé ici")); return; }
      setDrafts(Object.fromEntries(found.map((f) => [f.key, f.text || f.key])));
      setItems(found);
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, [editMode, lang, t]);

  const save = async () => {
    setSaving(true);
    try {
      for (const it of items) {
        const v = (drafts[it.key] || "").trim();
        if (v && v !== (it.text || it.key)) await saveEntry(it.key, v);
      }
      toast.success(t("Traduction enregistrée"));
      setItems(null);
    } catch { toast.error(t("Échec de l'enregistrement")); }
    finally { setSaving(false); }
  };

  return (
    <>
      {editMode && (
        <div data-translate-ui className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[60] flex items-center gap-3 rounded-full bg-[#2b2b2b] text-white px-4 py-2 text-sm shadow-lg" data-testid="translate-mode-bar">
          <span>{t("Mode traduction : cliquez sur un texte pour le corriger")} ({lang.toUpperCase()})</span>
          <Button size="sm" variant="secondary" className="h-7 rounded-full" onClick={() => setEditMode(false)} data-testid="translate-mode-exit">{t("Terminer")}</Button>
        </div>
      )}
      <Dialog open={!!items} onOpenChange={(o) => { if (!o) setItems(null); }}>
        <DialogContent data-translate-ui className="max-w-lg" data-testid="translate-dialog">
          <DialogTitle>{t("Corriger la traduction")}</DialogTitle>
          <DialogDescription>{t("Texte français d'origine, puis traduction affichée")} ({lang.toUpperCase()})</DialogDescription>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto">
            {(items || []).map((it, i) => (
              <div key={it.key} className="space-y-1">
                <div className="text-xs text-muted-foreground whitespace-pre-line">{it.key}</div>
                <Textarea rows={Math.min(6, Math.ceil((drafts[it.key] || "").length / 60) + 1)} value={drafts[it.key] || ""}
                  onChange={(e) => setDrafts((d) => ({ ...d, [it.key]: e.target.value }))} data-testid={`translate-input-${i}`} />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setItems(null)} data-testid="translate-cancel">{t("Annuler")}</Button>
            <Button onClick={save} disabled={saving} data-testid="translate-save">{t("Enregistrer")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
